import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Loader2, Save, Lock, Plus, Trash2, FolderOpen, ExternalLink, Upload, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { serviceDeliveryAPI, gdriveAPI } from '../lib/api';
import OurDriveCard from './OurDriveCard';

const uid = () => Math.random().toString(36).slice(2, 10);
export const DRIVE_TIPOS = {
  subir: { label: 'Para subir material', short: 'Subir', icon: Upload, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  consultar: { label: 'Para consultar', short: 'Consultar', icon: Eye, cls: 'bg-sky-50 text-sky-700 border-sky-200' },
};

function FolderRow({ f, RO, onChange, onDelete }) {
  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid={`drive-folder-${f.id}`}>
      <div className="flex items-center gap-2">
        <FolderOpen className="w-4 h-4 text-primary shrink-0" />
        <Input value={f.nombre} onChange={(e) => onChange({ nombre: e.target.value })} disabled={RO} placeholder="Nombre de la carpeta" className="h-9 font-medium" data-testid={`drive-folder-nombre-${f.id}`} />
        <div className="flex rounded-md border overflow-hidden shrink-0" data-testid={`drive-folder-tipo-${f.id}`}>
          {Object.entries(DRIVE_TIPOS).map(([k, t]) => (
            <button key={k} type="button" disabled={RO} onClick={() => onChange({ tipo: k })}
              className={`px-2.5 h-9 text-xs font-medium flex items-center gap-1 transition-colors ${f.tipo === k ? t.cls : 'bg-background text-muted-foreground hover:bg-muted'} ${RO ? 'cursor-default' : ''}`}
              data-testid={`drive-folder-tipo-${k}-${f.id}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.short}
            </button>
          ))}
        </div>
        {!RO && <Button variant="ghost" size="icon" className="h-9 w-9 text-red-500 shrink-0" onClick={onDelete} data-testid={`drive-folder-delete-${f.id}`}><Trash2 className="w-4 h-4" /></Button>}
      </div>
      <div className="flex items-center gap-2">
        <Input value={f.url} onChange={(e) => onChange({ url: e.target.value })} disabled={RO} placeholder="https://drive.google.com/drive/folders/..." className="h-9" data-testid={`drive-folder-url-${f.id}`} />
        {/^https?:\/\//i.test(f.url || '') && (
          <a href={f.url} target="_blank" rel="noreferrer" className="shrink-0" data-testid={`drive-folder-open-${f.id}`}>
            <Button variant="outline" size="sm" className="h-9"><ExternalLink className="w-4 h-4 mr-1" /> Abrir</Button>
          </a>
        )}
      </div>
      <Input value={f.nota} onChange={(e) => onChange({ nota: e.target.value })} disabled={RO} placeholder="Nota breve (opcional): para qué sirve esta carpeta" className="h-8 text-xs" data-testid={`drive-folder-nota-${f.id}`} />
      {f.auto && f.url && <p className="text-[11px] text-sky-700" data-testid={`drive-folder-auto-${f.id}`}>Vinculada automáticamente desde su Drive</p>}
      {!f.url && <p className="text-[11px] text-amber-600">Sin enlace: no se mostrará al cliente hasta que pegues el enlace.</p>}
    </div>
  );
}

export default function DriveFoldersSection({ clientId }) {
  const [folders, setFolders] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [ourDrive, setOurDrive] = useState(null);
  const [sharedRoot, setSharedRoot] = useState(null);
  const [rootUrl, setRootUrl] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await serviceDeliveryAPI.getDriveFolders(clientId);
      setFolders(r.folders || []);
      setCanEdit(!!r.can_edit);
      setOurDrive(r.our_drive || null);
      setSharedRoot(r.client_drive_root || null);
      setDirty(false);
    } catch { toast.error('Error cargando las carpetas'); setFolders([]); }
  }, [clientId]);
  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const syncRoot = async (url) => {
    if (dirty && !window.confirm('Tienes cambios sin guardar en las carpetas; se perderán. ¿Continuar?')) return;
    setSyncing(true);
    try {
      const r = await gdriveAPI.syncSharedRoot(clientId, url);
      setFolders(r.folders || []); setSharedRoot(r.client_drive_root); setDirty(false); setRootUrl('');
      toast.success(`${r.client_drive_root.subfolder_count} subcarpetas leídas · ${r.linked} vinculadas${r.added ? ` · ${r.added} nuevas` : ''}`);
      if (r.missing?.length) toast.warning(`Ya no están en su Drive: ${r.missing.join(', ')}`);
    } catch (e) { toast.error(e?.message || 'No se pudo leer la carpeta'); }
    finally { setSyncing(false); }
  };
  const mutate = (next) => { setFolders(next); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try { await serviceDeliveryAPI.saveDriveFolders(clientId, folders); toast.success('Carpetas guardadas · visibles en el portal del cliente'); await load(); }
    catch (e) { toast.error(e?.message || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  if (!folders) return <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  const RO = !canEdit;
  return (
    <div className="space-y-4">
    <OurDriveCard clientId={clientId} ourDrive={ourDrive} onCreated={setOurDrive} />
    <Card data-testid="drive-folders-section">
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2"><FolderOpen className="w-5 h-5 text-primary" /> Carpetas del cliente <Badge variant="outline" className="border-sky-300 text-sky-700">Su Drive · compartidas con nosotras</Badge></CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Las carpetas que el cliente nos comparte. Las que tienen enlace aparecen en su portal (Documentos) como accesos directos.</p>
          </div>
          {RO ? <Badge variant="secondary" className="gap-1"><Lock className="w-3 h-3" /> Solo lectura</Badge> : (
            <Button onClick={save} disabled={saving || !dirty} data-testid="drive-folders-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{dirty ? 'Guardar' : 'Guardado'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-3 space-y-2" data-testid="shared-root-box">
          <p className="text-xs font-semibold text-sky-800">Carpeta principal que nos ha compartido el cliente</p>
          {sharedRoot ? (
            <div className="flex items-center gap-2 flex-wrap">
              <a href={sharedRoot.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sm font-medium text-sky-800 hover:underline" data-testid="shared-root-link">
                <FolderOpen className="w-4 h-4" /> {sharedRoot.name || 'Carpeta del cliente'} <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <span className="text-[11px] text-muted-foreground" data-testid="shared-root-synced">{sharedRoot.subfolder_count} subcarpetas · leído {new Date(sharedRoot.synced_at).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              {!RO && (
                <Button size="sm" variant="outline" className="h-7 ml-auto" onClick={() => syncRoot(null)} disabled={syncing} data-testid="shared-root-resync">
                  {syncing && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Volver a leer subcarpetas
                </Button>
              )}
            </div>
          ) : <p className="text-xs text-muted-foreground">Pega el enlace de la carpeta de su Drive: se leerán sus subcarpetas y se vincularán solas (aquí y en su portal).</p>}
          {!RO && (
            <div className="flex gap-2">
              <Input value={rootUrl} onChange={(e) => setRootUrl(e.target.value)} placeholder={sharedRoot ? 'Cambiar por otro enlace…' : 'https://drive.google.com/drive/folders/...'} className="h-9 bg-white" data-testid="shared-root-input" />
              <Button size="sm" className="h-9 shrink-0" onClick={() => syncRoot(rootUrl)} disabled={syncing || !rootUrl.trim()} data-testid="shared-root-link-btn">
                {syncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FolderOpen className="w-4 h-4 mr-1" />} Vincular y leer subcarpetas
              </Button>
            </div>
          )}
        </div>
        {folders.length === 0 && <p className="text-sm text-muted-foreground italic">Sin carpetas.</p>}
        {folders.map((f) => (
          <FolderRow key={f.id} f={f} RO={RO}
            onChange={(patch) => mutate(folders.map((x) => (x.id === f.id ? { ...x, ...patch } : x)))}
            onDelete={() => mutate(folders.filter((x) => x.id !== f.id))} />
        ))}
        {!RO && (
          <Button variant="outline" size="sm" onClick={() => mutate([...folders, { id: uid(), nombre: '', url: '', nota: '', tipo: 'subir' }])} data-testid="drive-folder-add">
            <Plus className="w-4 h-4 mr-1" /> Añadir carpeta
          </Button>
        )}
      </CardContent>
    </Card>
    </div>
  );
}
